import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    Chip, FormControlLabel, Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip
} from "@mui/material";
import Spinner from "../../spinner/Spinner";
import CustomSnackbar from "../../alert/CustomSnackbar";
import {alertActions} from "../../../store/actions/alertActions";
import {connect} from "react-redux";
import {SettingsTabContentContainer, SettingsTabContentPaper} from "./Bot";
import {DeleteOutlined, EditOutlined, Info, ShieldOutlined} from "@mui/icons-material";
import {v4 as uuidv4} from "uuid";
import {botRulesActions} from "../../../store/actions/botRulesActions";
import AlertDialog from "../../alert/AlertDialog";
import {CustomTooltip} from "../../landings/LandingForm";
import {Controller, useForm} from "react-hook-form";
import BlurOverlayForBox from "../../utils/BlurOverlayForBox";
import {settingsServices} from "../../../service/settingsService";
import {SettingsEmptyState} from "../SettingsDesign";

function BotRules(props) {

    const {history, location, fetch, remove, errorMsg, successMsg} = props;
    const {rerender, botRules, isLoading} = props?.state?.botRules;

    const [prompt, setPrompt] = useState(false);
    const [deleteItem, setDeleteItem] = useState(null);

    const {control, watch, setValue} = useForm({defaultValues: {enableBotRules: false}});

    const handelAddEvent = () => {
        history.push({pathname: "/settings/bot-rules/add", state: {background: location}});
    };

    const handelEditEvent = (rowID) => {
        history.push({pathname: "/settings/bot-rules/edit/" + rowID, state: {background: location}});
    };

    const handleOnClickToPrompt = (row) => {
        setPrompt(true);
        setDeleteItem(row);
    };

    const handleOnClickToDelete = (confirm) => {
        if (!confirm) {
            setPrompt(false);
            setDeleteItem(null);
            return false;
        }

        remove(deleteItem?.id);
        setPrompt(false);
        setDeleteItem(null);
    };

    const getChip = (rules) => {
        let values = Object.keys(rules);
        if (values?.length > 3) {
            return <Chip key={uuidv4()} label={values?.length + " conditions..."} color="primary" size="small"
                         variant="outlined"/>;
        }
        return values?.map((v) => {
            return <Chip key={uuidv4()} label={v} color="primary" size="small" variant="outlined"/>;
        });
    };

    const handleClickOnEnableBotRules = async (data) => {
        props?.startLoadingSpinner();
        await settingsServices.updateCompanySettings({
            enableBotRules: data?.enableBotRules,
        }).then((response) => {
            if (response !== null && response !== undefined) {
                if (data?.enableBotRules) {
                    successMsg("Bot rules are enabled.");
                } else {
                    successMsg("Bot rules are disabled.");
                }
            }
        }).catch((error) => {
            if (error?.response?.status === 403) {
                errorMsg("Access forbidden. You have no rights to update bot rules settings.");
            } else if (error?.response?.data?.error) {
                errorMsg(error.response.data.error);
            }
        }).finally(() => {
            props?.stopLoadingSpinner();
        });
    };

    // Load all bot rules once when component is rendered
    useEffect(() => {
        if (rerender || botRules === null) {
            fetch();
        }
    }, [rerender, botRules, fetch]);

    useEffect(() => {
        async function fetchCompanySettings() {
            props?.startLoadingSpinner();
            await settingsServices.fetchCompanySettings().then((response) => {
                if (response !== null && response !== undefined) {
                    setValue("enableBotRules", response?.enableBotRules);
                }
            }).catch((error) => {
                if (error?.response?.status === 403) {
                    errorMsg("Access forbidden. You have no rights to fetch company settings.");
                } else if (error?.response?.data?.error) {
                    errorMsg(error.response.data.error);
                }
            }).finally(() => {
                props?.stopLoadingSpinner();
            });
        }

        fetchCompanySettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <SettingsTabContentContainer>
            <SettingsTabContentPaper elevation={0}>
                {isLoading && <Spinner size={32} overComponentBox={true}/>}

                {props?.state?.alert?.errorMsg !== "" &&
                    <CustomSnackbar message={props?.state?.alert?.errorMsg} severity="error" timeout={3500}/>
                }
                {props?.state?.alert?.successMsg !== "" &&
                    <CustomSnackbar message={props?.state?.alert?.successMsg} severity="success" timeout={3500}/>
                }

                {prompt &&
                    <AlertDialog
                        question="Delete confirmation"
                        text={"Are you sure you want to delete bot rule: " + deleteItem?.name + " ?"}
                        confirm={handleOnClickToDelete}
                    />
                }

                <Box display="flex">
                    <Box
                        flexGrow={1}
                        sx={{
                            fontSize: 16,
                            lineHeight: 1.167,
                            textAlign: "left",
                            fontWeight: 700,
                            paddingTop: 1,
                            paddingBottom: 2
                        }}
                    >
                        Bot rules
                    </Box>
                    <Box alignSelf="center" mr={1} sx={{position: "relative"}}>
                        <Button type="button" onClick={handelAddEvent} color="primary" variant="contained"
                                disabled={botRules?.length >= 25 || !watch("enableBotRules")}
                        >
                            Create ({botRules?.length} / 25)
                            {!watch("enableBotRules") && (
                                <BlurOverlayForBox/>
                            )}
                        </Button>
                    </Box>
                </Box>

                <Box sx={{color: "text.disabledLight", pb: 1, pt: 1}}>
                    Stop bots from messing up your campaign data. Set up automatic rules to flag and separate bot
                    activity from real users in real-time.
                </Box>

                <Box>
                    <Controller
                        name="enableBotRules"
                        control={control}
                        render={({field: {onChange, value, ...props}}) => (
                            <FormControlLabel
                                control={
                                    <Switch
                                        onChange={(e) => {
                                            onChange(e?.target?.checked);
                                            handleClickOnEnableBotRules({enableBotRules: e?.target?.checked});
                                        }}
                                        checked={value}
                                        color="primary"
                                    />
                                }
                                label={(
                                    <span>
                                        <span>{value ? "Disable" : "Enable"} Bot Rules</span>
                                        <CustomTooltip
                                            disableFocusListener
                                            title="Enabling custom bot rules may slow down your redirect speed for campaigns."
                                        >
                                            <Info color={"primary"} fontSize={"small"}/>
                                        </CustomTooltip>
                                    </span>
                                )}
                            />)}
                        defaultValue={false}
                    />
                </Box>

                <Box sx={{
                    width: "100%",
                    overflow: "auto",
                    whiteSpace: "nowrap"
                }}>
                    <Box display="flex" flexDirection="row" flexWrap="wrap" mt={1} sx={{position: "relative"}}>
                        {!watch("enableBotRules") && botRules?.length > 0 && (
                            <BlurOverlayForBox/>
                        )}
                        <Box mt={1} flexGrow={1}>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Conditions</TableCell>
                                            <TableCell align="left">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    {botRules?.length > 0 &&
                                        <TableBody sx={{
                                            overflow: "auto",
                                            whiteSpace: "nowrap",
                                        }}>
                                            {props?.state?.botRules?.botRules?.map((row, idx) => (
                                                <TableRow key={row?.id}>
                                                    <TableCell sx={{
                                                        minWidth: "250px",
                                                        maxWidth: "250px",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}>
                                                        {row?.name}
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        minWidth: "250px",
                                                        maxWidth: "250px",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}>
                                                        {getChip(row?.rules)}
                                                    </TableCell>

                                                    <TableCell>
                                                        {!row?.isDeleted &&
                                                            <Box display="flex" flexDirection="row"
                                                                 flexWrap="noWrap"
                                                                 justifyContent="right">
                                                                <Box>
                                                                    <Tooltip title="Edit">
                                                                        <EditOutlined
                                                                            color="inherit"
                                                                            fontSize="small"
                                                                            sx={{cursor: "pointer"}}
                                                                            onClick={() => handelEditEvent(row?.id)}
                                                                        />
                                                                    </Tooltip>
                                                                </Box>
                                                                <Box ml={2}>
                                                                    <Tooltip title="Delete">
                                                                        <DeleteOutlined
                                                                            color="error"
                                                                            fontSize="small"
                                                                            sx={{cursor: "pointer"}}
                                                                            onClick={() => handleOnClickToPrompt(row)}
                                                                        />
                                                                    </Tooltip>
                                                                </Box>
                                                            </Box>
                                                        }
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    }
                                </Table>
                                {!isLoading && !botRules?.length && <SettingsEmptyState
                                    icon={ShieldOutlined} title="No bot rules yet"
                                    description={watch('enableBotRules')
                                        ? 'Create your first rule to identify and filter bot traffic.'
                                        : 'Enable bot rules, then create your first rule to identify and filter bot traffic.'}/>}
                            </TableContainer>
                        </Box>
                    </Box>
                </Box>
            </SettingsTabContentPaper>
        </SettingsTabContentContainer>
    );
}

const mapStateToProps = (state) => {
    return {
        state: {
            alert: state.alert,
            botRules: state.botRules,
        }
    };
};

const mapDispatchToProps = {
    clearMsg: alertActions.clear,
    errorMsg: alertActions.error,
    successMsg: alertActions.success,
    fetch: botRulesActions.fetch,
    remove: botRulesActions.remove,
    startLoadingSpinner: botRulesActions.startLoading,
    stopLoadingSpinner: botRulesActions.stopLoading,
};

export default connect(mapStateToProps, mapDispatchToProps)(BotRules);
